import {
  buildCldImage,
  CloudinaryQuality,
  DOWNLOAD_OPTIMIZED_KEEP_EXIF,
  resolveImageUrl,
  type CldResolvable,
} from './cloudinary';

/**
 * URL de DESCARGA de una foto: la que usan el visor a pantalla completa y el
 * ZIP de descarga múltiple. Es lo que se entrega a un tercero.
 *
 * Vive aparte de `resolveImageUrl` —que decide qué se PINTA— porque las dos
 * preguntas ya no tienen la misma respuesta (spec 2026-09-23, fotos
 * geolocalizadas):
 *
 *  - **Alta calidad → el ORIGINAL de Hetzner.** El original ya viene en la
 *    caja de 2560 px desde agosto, es JPEG y lleva íntegro el EXIF que
 *    escribió la cámara (GPS, fecha de captura). De paso ahorra una
 *    transformación por descarga.
 *  - **Optimizada → Cloudinary (1024 px) CON el EXIF** (§ 17): calidad fija
 *    + `fl_keep_iptc`, ver `DOWNLOAD_OPTIMIZED_KEEP_EXIF`. Cuando salga la
 *    rama de derivadas la sustituye `dl1024`, y esta queda de respaldo.
 *  - Legacy de Cloudinary (sin original en Hetzner): su camino de siempre.
 *    Pasaron por el `canvas` antes de subir, así que no tienen EXIF que
 *    conservar; y si alguna trajera `Orientation`, conservarla después de
 *    que Cloudinary la gire la giraría dos veces.
 */
export const resolveDownloadUrl = (
  image: CldResolvable | null | undefined,
  quality: CloudinaryQuality,
): string | null => {
  if (!image) return null;
  if (quality === CloudinaryQuality.High && image.publicUrl) {
    return image.publicUrl;
  }
  if (quality === CloudinaryQuality.Optimized && image.publicUrl) {
    const cldImg = buildCldImage(image);
    if (cldImg) return cldImg.addTransformation(DOWNLOAD_OPTIMIZED_KEEP_EXIF).toURL();
  }
  return resolveImageUrl(
    image,
    quality === CloudinaryQuality.High ? 'full' : 'thumb',
  );
};

/**
 * El ORIGINAL de Hetzner servido por Cloudinary SIN transformar. Medido el
 * 2026-09-25: son los mismos bytes que el bucket, con el EXIF entero (GPS
 * incluido). Sirve de respaldo cuando el bucket no responde pero Cloudinary sí
 * (que tiene su propia copia en caché). Las legacy no tienen original en
 * Hetzner: sin respaldo extra.
 */
const cloudinaryOriginalUrl = (image: CldResolvable | null | undefined): string | null => {
  if (!image?.publicUrl) return null;
  return buildCldImage(image)?.toURL() ?? null;
};

/**
 * Baja el blob de una foto para guardarlo o meterlo en el ZIP, con RESPALDO.
 *
 * Prueba, en orden y sin repetir: la URL de `resolveDownloadUrl`, el ORIGINAL
 * de Hetzner (`publicUrl`), ese mismo original servido por Cloudinary sin
 * transformar, y las URLs extra que pase el llamante (p. ej. el camino legacy
 * del visor). La primera que responda 2xx gana.
 *
 * 🔴 Las del llamante van las ÚLTIMAS: la del visor es `q_auto:best`, que
 * borra el GPS (spec fotos geolocalizadas § 17.5). Mejor una foto sin GPS que
 * ninguna, pero solo cuando no queda otra.
 *
 * Por qué hace falta (revisión del 2026-09-23): la URL elegida puede fallar
 * sin que la foto falte — Cloudinary sin créditos, o un endpoint que responde
 * con una redirección al bucket, que el navegador marca con `Origin: null` y el
 * CORS del bucket rechaza. El original va directo al bucket, con CORS permitido
 * para todos los orígenes de la app, y lleva el EXIF. Y un 4xx/5xx deja de
 * guardarse como si fuera una imagen.
 *
 * Las peticiones van con `cache: 'no-store'` (ver el comentario de dentro).
 */
export async function fetchDownloadBlob(
  image: CldResolvable | null | undefined,
  quality: CloudinaryQuality,
  fallbacks: ReadonlyArray<string | null | undefined> = [],
): Promise<Blob> {
  const candidates = [
    resolveDownloadUrl(image, quality),
    image?.publicUrl,
    cloudinaryOriginalUrl(image),
    ...fallbacks,
  ].filter(
    (url, index, all): url is string => !!url && all.indexOf(url) === index,
  );
  if (candidates.length === 0) {
    throw new Error('fetchDownloadBlob: la imagen no tiene ninguna URL de descarga');
  }
  let lastError: unknown;
  for (const url of candidates) {
    try {
      // 🔴 Sin caché HTTP: el bucket solo manda la cabecera CORS si la petición
      // trae Origin, y un <img> no la trae. Si la foto ya se pintó, su
      // respuesta está en caché SIN cabecera CORS y un fetch normal se sirve
      // de ahí y falla sin llegar al servidor (reproducido en Chromium).
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`fetchDownloadBlob: HTTP ${response.status} en ${url}`);
      }
      return await response.blob();
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
}
